import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format } from "date-fns";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import img from "./all_bg.jpg";

const BACKEND_URL = "https://api.bbsfacultyprofile.com";
const TABLE_NAME = "committee_service";

const CommitteeService = () => {
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);
  const [recordId, setRecordId] = useState(null);

  const [formData, setFormData] = useState({
    year: null,
    year_ending: null,
    committee_name: "",
    institution: "",
    committee_role: "",
    contributing_industry: "",
    country: "",
  });

  const fetchRecords = async () => {
  try {
    const res = await fetch(`${BACKEND_URL}/api/${TABLE_NAME}`, {
      method: "GET",
      credentials: "include",
    });

    if (!res.ok) throw new Error("Failed to fetch records");

    const data = await res.json();

    const processed = Array.isArray(data)
      ? data
          .map((rec) => ({
            ...rec,
            year: rec.year ? new Date(rec.year) : null,
            year_ending: rec.year_ending ? new Date(rec.year_ending) : null,
          }))
          .sort((a, b) => {
            if (!a.year) return 1;      // Put empty dates at the bottom
            if (!b.year) return -1;
            return b.year - a.year;     // Latest starting date first
          })
      : [];

    setRecords(processed);
  } catch (err) {
    toast.error(err.message);
  }
};

  useEffect(() => {
    fetchRecords();
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const payload = {
        ...formData,
        year: formData.year ? format(formData.year, "yyyy-MM-dd") : null,
        year_ending: formData.year_ending
          ? format(formData.year_ending, "yyyy-MM-dd")
          : null,
      };

      const url = recordId
        ? `${BACKEND_URL}/api/${TABLE_NAME}/${recordId}`
        : `${BACKEND_URL}/api/${TABLE_NAME}`;

      const method = recordId ? "PUT" : "POST";

      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      if (!res.ok) throw new Error("Failed to save record");

      toast.success(
        recordId ? "Record updated successfully!" : "Record added successfully!"
      );

      setFormData({
        year: null,
        year_ending: null,
        committee_name: "",
        institution: "",
        committee_role: "",
        contributing_industry: "",
        country: "",
      });

      setRecordId(null);
      fetchRecords();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleEdit = (rec) => {
    setFormData({
      year: rec.year ? new Date(rec.year) : null,
      year_ending: rec.year_ending ? new Date(rec.year_ending) : null,
      committee_name: rec.committee_name || "",
      institution: rec.institution || "",
      committee_role: rec.committee_role || "",
      contributing_industry: rec.contributing_industry || "",
      country: rec.country || "",
    });
    setRecordId(rec.id);
    toast.info("Editing selected record...");
  };

  const handleDelete = (rec) => {
    const toastId = toast.info(
      <div>
        Delete this record?
        <button
          onClick={async () => {
            try {
              const res = await fetch(
                `${BACKEND_URL}/api/${TABLE_NAME}/${rec.id}`,
                { method: "DELETE", credentials: "include" }
              );
              if (!res.ok) throw new Error("Failed to delete record");
              fetchRecords();
              toast.update(toastId, {
                render: "Record deleted successfully!",
                type: "success",
                autoClose: 3000,
              });
            } catch (err) {
              toast.update(toastId, {
                render: `Delete error: ${err.message}`,
                type: "error",
                autoClose: 3000,
              });
            }
          }}
          style={{
            marginLeft: "10px",
            padding: "3px 8px",
            background: "#ef4444",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
          }}
        >
          Confirm
        </button>
      </div>,
      { autoClose: false, closeOnClick: false }
    );
  };

  const inputStyle = {
    padding: "10px",
    marginTop: "5px",
    border: "1px solid #ccc",
    borderRadius: "6px",
    width: "100%",
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        padding: "40px 20px",
        fontFamily: "Arial, sans-serif",
        backgroundImage: `url(${img})`,
        backgroundSize: "cover",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "center",
        backgroundAttachment: "fixed",
      }}
    >
      <ToastContainer position="top-center" autoClose={3000} />

      <div
        style={{
          maxWidth: "800px",
          margin: "0 auto",
          background: "#fff",
          padding: "30px 40px",
          borderRadius: "10px",
          boxShadow: "0px 0px 12px rgba(0,0,0,0.1)",
        }}
      >
        <h2
          style={{
            textAlign: "center",
            color: "#1e3a8a",
            marginBottom: "25px",
          }}
        >
          Committee Services
        </h2>

        <form
          onSubmit={handleSubmit}
          style={{ display: "flex", flexDirection: "column" }}
        >
          {/* Dates Row */}
          <div style={{ display: "flex", gap: "10px" }}>
            <div style={{ flex: 1 }}>
              <label style={{ fontWeight: "bold" }}>
                Starting Date (mm/yyyy)
              </label>
              <DatePicker
                selected={formData.year}
                onChange={(date) => setFormData({ ...formData, year: date })}
                dateFormat="MM/yyyy"
                showMonthYearPicker
                placeholderText="Select month/year"
                style={{ width: "100%" }}
              />
            </div>

            <div style={{ flex: 1 }}>
              <label style={{ fontWeight: "bold" }}>
                Ending Date (mm/yyyy)
              </label>
              <DatePicker
                selected={formData.year_ending}
                onChange={(date) =>
                  setFormData({ ...formData, year_ending: date })
                }
                dateFormat="MM/yyyy"
                showMonthYearPicker
                placeholderText="Select month/year"
                style={{ width: "100%" }}
              />
            </div>
          </div>

          {/* Committee Name */}
          <div>
            <label style={{ fontWeight: "bold", marginTop: "10px" }}>
              Committee Name
            </label>
            <input
              type="text"
              name="committee_name"
              value={formData.committee_name}
              onChange={handleChange}
              style={inputStyle}
              required
            />
          </div>

          {/* Institution */}
          <div>
            <label style={{ fontWeight: "bold", marginTop: "10px" }}>
              Institution
            </label>
            <input
              type="text"
              name="institution"
              value={formData.institution}
              onChange={handleChange}
              style={inputStyle}
              required
            />
          </div>
          {/* Role Dropdown */}
          <div>
            <label style={{ fontWeight: "bold", marginTop: "10px" }}>
              Role
            </label>
            <select
              name="committee_role"
              value={formData.committee_role}
              onChange={handleChange}
              style={inputStyle}
              required
            >
              <option value="">Select Role</option>
              <option value="Lead">Lead</option>
              <option value="Member">Member</option>
            </select>
          </div>

          {/* Renamed to Major Activities */}
          <div>
            <label style={{ fontWeight: "bold", marginTop: "10px" }}>
              Major Activities
            </label>
            <input
              type="text"
              name="contributing_industry"
              value={formData.contributing_industry}
              onChange={handleChange}
              style={inputStyle}
              required
            />
          </div>

          {/* Country */}
          <div>
            <label style={{ fontWeight: "bold", marginTop: "10px" }}>
              Country
            </label>
            <input
              type="text"
              name="country"
              value={formData.country}
              onChange={handleChange}
              style={inputStyle}
              required
            />
          </div>

          <button
            type="submit"
            style={{
              background: "#3b82f6",
              color: "white",
              padding: "12px",
              border: "none",
              borderRadius: "6px",
              marginTop: "20px",
              cursor: "pointer",
              fontWeight: "bold",
            }}
          >
            {recordId ? "Update Record" : "Add Record"}
          </button>
        </form>

        {/* Records Table */}
        {records.length > 0 && (
          <div style={{ marginTop: "30px", overflowX: "auto" }}>
            <h3 style={{ color: "#1e3a8a" }}>Your Committee Services</h3>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                marginTop: "10px",
              }}
            >
              <thead>
                <tr>
                  {[
                    "Starting Date",
                    "Ending Date",
                    "Committee Name",
                    "Institution",
                    "Role",
                    "Major Activities",
                    "Country",
                    "Actions",
                  ].map((h) => (
                    <th
                      key={h}
                      style={{
                        border: "1px solid #ccc",
                        padding: "8px",
                        background: "#f1f5f9",
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {records.map((rec) => (
                  <tr key={rec.id}>
                    <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                      {rec.year ? format(new Date(rec.year), "MM/yyyy") : ""}
                    </td>
                    <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                      {rec.year_ending
                        ? format(new Date(rec.year_ending), "MM/yyyy")
                        : ""}
                    </td>
                    <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                      {rec.committee_name}
                    </td>
                    <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                      {rec.institution}
                    </td>
                    <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                      {rec.committee_role}
                    </td>
                    <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                      {rec.contributing_industry}
                    </td>
                    <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                      {rec.country}
                    </td>
                    <td
                      style={{
                        border: "1px solid #ccc",
                        padding: "8px",
                        textAlign: "center",
                      }}
                    >
                      <div style={{ display: "inline-flex", gap: "5px" }}>
                        <button
                          onClick={() => handleEdit(rec)}
                          style={{
                            background: "#2563eb",
                            color: "white",
                            border: "none",
                            borderRadius: "5px",
                            padding: "5px 10px",
                            cursor: "pointer",
                          }}
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(rec)}
                          style={{
                            background: "#ef4444",
                            color: "white",
                            border: "none",
                            borderRadius: "5px",
                            padding: "5px 10px",
                            cursor: "pointer",
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div style={{ textAlign: "center", marginTop: "25px" }}>
          <button
            onClick={() => navigate("/dashboard")}
            style={{
              background: "#1e3a8a",
              color: "white",
              padding: "10px 20px",
              border: "none",
              borderRadius: "6px",
              cursor: "pointer",
              fontWeight: "bold",
            }}
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    </div>
  );
};

export default CommitteeService;
